/* Glizm — "Buy more, save more" bundle picker for Castivaro-style pages.
   The picked pack sets the product form's quantity. When a multi-variant
   product is bought as a pack with different variants per item, the items
   are added together through the cart API. The discount itself comes from
   the product's automatic discounts in Shopify. */
if (!customElements.get('cv-bundle')) {
  customElements.define(
    'cv-bundle',
    class CvBundle extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.form);
        this.qty = this.querySelector('[data-qty]');
        this.tiers = [...this.querySelectorAll('[data-tier]')];
        this.tiers.forEach((tier) => {
          tier.addEventListener('click', (event) => {
            if (event.target.closest('select')) return;
            this.select(tier);
          });
          tier.addEventListener('change', (event) => {
            if (event.target.closest('select')) this.select(tier);
          });
        });
        this.select(this.querySelector('.cv-bundle__tier.is-selected') || this.tiers[0]);

        // This page has its own bundle offer, so remove the Kaching Bundles box
        // (it would add a second, conflicting quantity picker).
        const info = this.closest('.product__info-container') || document;
        const removeKaching = () => info.querySelectorAll('kaching-bundle, kaching-bundles-block').forEach((el) => el.remove());
        removeKaching();
        this.kachingObserver = new MutationObserver(removeKaching);
        this.kachingObserver.observe(info, { childList: true, subtree: true });

        this.onSubmit = this.onSubmit.bind(this);
        document.addEventListener('submit', this.onSubmit, true);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const data = event.data || {};
            if (data.sectionId !== this.dataset.section || !data.variant) return;
            this.dataset.price = data.variant.price;
            this.dataset.compare = data.variant.compare_at_price || data.variant.price;
            this.querySelectorAll('[data-pick]').forEach((select) => (select.value = String(data.variant.id)));
            this.refreshPrices();
          });
        }
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
        if (this.unsubscribe) this.unsubscribe();
        if (this.kachingObserver) this.kachingObserver.disconnect();
      }

      select(tier) {
        this.tiers.forEach((t) => {
          const on = t === tier;
          t.classList.toggle('is-selected', on);
          const radio = t.querySelector('input[type="radio"]');
          if (radio) radio.checked = on;
        });
        this.current = tier;
        this.qty.value = tier.dataset.tier;
      }

      picks() {
        return [...this.current.querySelectorAll('[data-pick]')].map((select) => select.value);
      }

      onSubmit(event) {
        if (!this.form || event.target !== this.form) return;
        const picks = this.picks();
        this.qty.value = this.current.dataset.tier;
        if (picks.length === 0) return;

        const idInput = this.form.querySelector('input[name="id"]');
        const unique = [...new Set(picks)];
        if (unique.length === 1) {
          if (idInput) idInput.value = unique[0];
          return;
        }

        // Mixed variants: add each one with its own quantity.
        event.preventDefault();
        event.stopImmediatePropagation();
        const counts = picks.reduce((all, id) => ((all[id] = (all[id] || 0) + 1), all), {});
        const items = Object.entries(counts).map(([id, quantity]) => ({ id: Number(id), quantity }));
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items };
        if (cart && cart.getSectionsToRender) {
          body.sections = cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }
        const button = this.form.querySelector('[type="submit"]');
        if (button) button.setAttribute('aria-disabled', 'true');
        fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) throw new Error(response.description || response.message);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cv-bundle', cartData: response });
            }
            if (cart && cart.renderContents && response.sections) {
              cart.renderContents({ ...response, id: items[0].id });
              if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            } else {
              window.location.href = `${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart`;
            }
          })
          .catch(() => {
            window.location.href = `${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart`;
          })
          .finally(() => {
            if (button) button.removeAttribute('aria-disabled');
          });
      }

      money(cents) {
        // Shop money format can contain HTML (currency app); textContent needs plain text
        const format = (this.dataset.money || '${{amount}}').replace(/<[^>]*>/g, '');
        const value = (cents / 100).toFixed(2);
        const [whole, decimals] = value.split('.');
        const comma = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const dot = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          switch (key) {
            case 'amount_no_decimals':
              return comma;
            case 'amount_with_comma_separator':
              return `${dot},${decimals}`;
            case 'amount_no_decimals_with_comma_separator':
              return dot;
            default:
              return `${comma}.${decimals}`;
          }
        });
      }

      refreshPrices() {
        const price = Number(this.dataset.price);
        const compare = Number(this.dataset.compare) || price;
        this.tiers.forEach((tier) => {
          const qty = Number(tier.dataset.tier);
          const each = Math.round((price * (100 - Number(tier.dataset.pct))) / 100);
          const total = each * qty;
          const was = compare * qty;
          const set = (selector, text) => {
            const el = tier.querySelector(selector);
            if (el) el.textContent = text;
          };
          set('[data-total]', this.money(total));
          set('[data-each]', this.money(each));
          set('[data-was]', this.money(was));
          if (was > total) set('[data-save]', `SAVE ${Math.round(((was - total) * 100) / was)}%`);
        });
      }
    }
  );
}
